import { assign, createMachine } from "xstate";
import { updateEmail } from "@nju/data-access-portal-user";
import { BasicError, isError } from "@nju/result";
import { isValidEmail } from "@nju/utils";

interface IContext {
  oldEmail: string;
  email?: string;
  emailError?: string;
}

type IEvent =
  | { type: "SET_EMAIL"; email: string }
  | { type: "SUBMIT" }
  | { type: "SUCCESS" }
  | { type: "ERROR" }
  | { type: "VALIDATE" }
  | { type: "EMAIL_TAKEN" };

const validateEmailFormat = (email = "") => {
  const validateFormat = isValidEmail(email)
    ? undefined
    : "Podaj poprawny e-mail.";

  return email ? validateFormat : "Podaj e-mail.";
};

export const changeEmailMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      oldEmail: "",
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_EMAIL: {
            actions: "setEmail",
          },
          SUBMIT: {
            actions: "validateBeforeSend",
            target: "validate",
          },
        },
      },
      validate: {
        always: [
          {
            cond: "isEmailError",
            target: "send",
          },
          { target: "ready" },
        ],
      },
      send: {
        invoke: {
          src: "sendEmail",
        },
        on: {
          EMAIL_TAKEN: {
            actions: "setEmailTakenError",
            target: "ready",
          },
          SUCCESS: {
            actions: "onSuccessSend",
          },
          ERROR: {
            actions: "onErrorSend",
          },
        },
      },
    },
  },
  {
    guards: {
      isEmailError: (context) => {
        return !!context.email && !context.emailError;
      },
    },
    actions: {
      setEmail: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_EMAIL") {
          return {
            email: event.email,
            emailError: validateEmailFormat(event.email),
          };
        }
        return context;
      }),
      setEmailTakenError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "EMAIL_TAKEN") {
          return {
            emailError: "Istnieje już konto założone na ten e-mail.",
          };
        }
        return context;
      }),
      validateBeforeSend: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SUBMIT") {
          if (context.email?.toLowerCase() === context.oldEmail.toLowerCase()) {
            return {
              emailError: "Istnieje już konto założone na ten e-mail.",
            };
          }

          return {
            emailError: validateEmailFormat(context.email),
          };
        }
        return context;
      }),
    },
    services: {
      sendEmail: ({ oldEmail, email }) => {
        return async (callback) => {
          if (!email) {
            throw new BasicError({ message: "Missing email" });
          }

          const result = await updateEmail(oldEmail, email);

          if (isError(result)) {
            if (result.err.code === "EMAIL_ALREADY_TAKEN") {
              callback({ type: "EMAIL_TAKEN" });
            }

            return callback({ type: "ERROR" });
          }

          return callback({
            type: "SUCCESS",
          });
        };
      },
    },
  },
);
